import React, { useState, useEffect } from 'react';
import { useFavorites } from '../context/FavoritesContext';
import { useParcelContext } from '../context/ParcelContext';
import styled from 'styled-components';
import { FaHeart, FaTrash, FaFileExcel, FaFileCsv, FaMapMarkerAlt, FaCalendarAlt, FaDollarSign, FaRuler } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { api } from '../services/api';

const FavoritesContainer = styled.div`
  flex: 1;
  padding: 1rem;
  background: white;
  overflow-y: auto;
`;

const FavoritesHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #ff4757;
`;

const FavoritesTitle = styled.h3`
  margin: 0;
  color: #333;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ExportButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ExportButton = styled.button`
  padding: 0.25rem 0.5rem;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8rem;
  
  &:hover {
    background: #e9e9e9;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #666;
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.3;
`;

const FavoriteItem = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
  background: #fafafa;
  position: relative;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f0f0f0;
    border-color: #667eea;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
`;

const ItemTitle = styled.h4`
  margin: 0;
  color: #333;
  font-size: 1rem;
`;

const RemoveButton = styled.button`
  background: #ff4757;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  font-size: 0.8rem;
  
  &:hover {
    background: #ff3742;
  }
`;

const ItemDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  font-size: 0.9rem;
`;

const DetailField = styled.div`
  strong {
    color: #666;
  }
`;

const TypeBadge = styled.span`
  background: ${props => props.type === 'parcel' ? '#667eea' : '#2ed573'};
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  margin-left: 0.5rem;
`;

const LoadingSpinner = styled.div`
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const FavoritesPanel = ({ onFavoriteClick: setViewState, onSetParcelBorder: setParcelBorder }) => {
  const { 
    favorites, 
    loading, 
    error, 
    totalCount, 
    hasMore,
    getFavorites, 
    deleteFavorite, 
    loadMoreFavorites 
  } = useFavorites();
  
  const { 
    filters
  } = useParcelContext();
  
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    getFavorites();
  }, [getFavorites]);

  // Helper function to fetch detailed parcel data
  const fetchParcelDetails = async (parcelId, state = 'ca', county = 'orange') => {
    try {
      const result = await api.getParcelDetails(parcelId, state, county);
      return result.success ? result.parcel : null;
    } catch (error) {
      console.error(`Error fetching details for parcel ${parcelId}:`, error);
      return null;
    }
  };

  // Helper function to get state and county from filters
  const getStateAndCounty = () => {
    const stateCode = filters.state || 'ca';
    const countyName = filters.county || 'orange';
    return { stateCode, countyName };
  };

  const exportToExcel = async () => {
    setExporting(true);
    try {
      const { stateCode, countyName } = getStateAndCounty();
      const exportData = [];
      
      // Show progress feedback
      console.log(`Starting export of ${favorites.length} favorites...`);

      for (const favorite of favorites) {
        // Normalize parcel IDs from favorite
        let parcelIds = [];
        if (typeof favorite.parcel_ids === 'string') {
          try {
            parcelIds = JSON.parse(favorite.parcel_ids);
            if (!Array.isArray(parcelIds)) {
              parcelIds = [favorite.parcel_ids];
            }
          } catch {
            parcelIds = [favorite.parcel_ids];
          }
        } else if (Array.isArray(favorite.parcel_ids)) {
          parcelIds = favorite.parcel_ids;
        } else if (favorite.parcel_ids) {
          parcelIds = [favorite.parcel_ids];
        }
        
        const isAssemblage = parcelIds.length > 1;

        if (isAssemblage) {
          // For assemblages, create one row with all parcel IDs and FLU info
          const parcelDetails = [];
          for (const parcelId of parcelIds) {
            const details = await fetchParcelDetails(parcelId, stateCode, countyName);
            if (details) {
              parcelDetails.push({
                parcel_id: parcelId,
                land_use_category: details.land_use_category,
                zoning: details.zoning,
                land_acres: details.land_acres,
                assd_imp: details.assd_imp,
                addr: details.addr,
                owner: details.owner
              });
            }
          }

          // Create assemblage row with all parcel details
          const assemblageRow = {
            'Favorite ID': favorite.id,
            'Type': 'Assemblage',
            'Assemblage Tag': `ASM-${favorite.id}`, // Assemblage identifier tag
            'Number of Parcels': parcelIds.length,
            'All Parcel IDs': parcelIds.join(', '),
            'Total Size (Acres)': favorite.size_acres || 'N/A',
            'Future Land Use': favorite.future_land_use || 'N/A',
            'In Place Zoning': favorite.in_place_zoning || 'N/A',
            'Total Improvement Value': favorite.total_improvement_value ? `$${favorite.total_improvement_value.toLocaleString()}` : 'N/A',
            'Center Point': favorite.center_point ? `${favorite.center_point[0]}, ${favorite.center_point[1]}` : 'N/A',
            'Created At': new Date(favorite.created_at).toLocaleDateString(),
            'Updated At': new Date(favorite.updated_at).toLocaleDateString()
          };

          // Add individual parcel details as separate columns
          parcelDetails.forEach((parcel, index) => {
            assemblageRow[`Parcel ${index + 1} ID`] = parcel.parcel_id;
            assemblageRow[`Parcel ${index + 1} FLU`] = parcel.land_use_category || 'N/A';
            assemblageRow[`Parcel ${index + 1} Zoning`] = parcel.zoning || 'N/A';
            assemblageRow[`Parcel ${index + 1} Acres`] = parcel.land_acres || 'N/A';
            assemblageRow[`Parcel ${index + 1} Address`] = parcel.addr || 'N/A';
            assemblageRow[`Parcel ${index + 1} Owner`] = parcel.owner || 'N/A';
          });

          exportData.push(assemblageRow);

          // Also add individual rows for each parcel in the assemblage with assemblage tag
          parcelDetails.forEach((parcel, index) => {
            const individualParcelRow = {
              'Favorite ID': favorite.id,
              'Type': 'Individual Parcel (Part of Assemblage)',
              'Assemblage Tag': `ASM-${favorite.id}`, // Same assemblage identifier
              'Parcel ID': parcel.parcel_id,
              'Size (Acres)': parcel.land_acres || 'N/A',
              'Future Land Use': parcel.land_use_category || 'N/A',
              'In Place Zoning': parcel.zoning || 'N/A',
              'Total Improvement Value': parcel.assd_imp ? `$${parcel.assd_imp.toLocaleString()}` : 'N/A',
              'Address': parcel.addr || 'N/A',
              'Owner': parcel.owner || 'N/A',
              'Center Point': favorite.center_point ? `${favorite.center_point[0]}, ${favorite.center_point[1]}` : 'N/A',
              'Created At': new Date(favorite.created_at).toLocaleDateString(),
              'Updated At': new Date(favorite.updated_at).toLocaleDateString(),
              'Parcel Position in Assemblage': `${index + 1} of ${parcelIds.length}`
            };
            exportData.push(individualParcelRow);
          });
        } else {
          // For single parcels, fetch detailed data
          const parcelId = parcelIds[0];
          const parcelDetails = await fetchParcelDetails(parcelId, stateCode, countyName);

          const singleParcelRow = {
            'Favorite ID': favorite.id,
            'Type': 'Single Parcel',
            'Assemblage Tag': 'N/A', // Not part of an assemblage
            'Parcel ID': parcelId,
            'Size (Acres)': favorite.size_acres || parcelDetails?.land_acres || 'N/A',
            'Future Land Use': favorite.future_land_use || parcelDetails?.land_use_category || 'N/A',
            'In Place Zoning': favorite.in_place_zoning || parcelDetails?.zoning || 'N/A',
            'Total Improvement Value': favorite.total_improvement_value ? `$${favorite.total_improvement_value.toLocaleString()}` : 
                                     (parcelDetails?.assd_imp ? `$${parcelDetails.assd_imp.toLocaleString()}` : 'N/A'),
            'Center Point': favorite.center_point ? `${favorite.center_point[0]}, ${favorite.center_point[1]}` : 'N/A',
            'Created At': new Date(favorite.created_at).toLocaleDateString(),
            'Updated At': new Date(favorite.updated_at).toLocaleDateString()
          };

          // Add full parcel data if available
          if (parcelDetails) {
            singleParcelRow['Address'] = parcelDetails.addr || 'N/A';
            singleParcelRow['Owner'] = parcelDetails.owner || 'N/A';
            singleParcelRow['City'] = parcelDetails.city || 'N/A';
            singleParcelRow['State'] = parcelDetails.state || 'N/A';
            singleParcelRow['Zip Code'] = parcelDetails.zip || 'N/A';
            singleParcelRow['Assessed Land Value'] = parcelDetails.assd_lan ? `$${parcelDetails.assd_lan.toLocaleString()}` : 'N/A';
            singleParcelRow['Market Total Value'] = parcelDetails.mkt_val ? `$${parcelDetails.mkt_val.toLocaleString()}` : 'N/A';
            singleParcelRow['Market Land Value'] = parcelDetails.mkt_lan ? `$${parcelDetails.mkt_lan.toLocaleString()}` : 'N/A';
            singleParcelRow['Market Improvement Value'] = parcelDetails.mkt_imp ? `$${parcelDetails.mkt_imp.toLocaleString()}` : 'N/A';
            singleParcelRow['Tax Amount'] = parcelDetails.tax_amt ? `$${parcelDetails.tax_amt.toLocaleString()}` : 'N/A';
            singleParcelRow['Tax Year'] = parcelDetails.tax_yr || 'N/A';
            singleParcelRow['Assessed Year'] = parcelDetails.assd_yr || 'N/A';
            singleParcelRow['Census Tract'] = parcelDetails.census_tr || 'N/A';
            singleParcelRow['APN'] = parcelDetails.apn || 'N/A';
            singleParcelRow['Formatted APN'] = parcelDetails.frm_apn || 'N/A';
            singleParcelRow['Original APN'] = parcelDetails.orig_apn || 'N/A';
            singleParcelRow['Account Number'] = parcelDetails.acct_no || 'N/A';
            singleParcelRow['Owner 1 Last Name'] = parcelDetails.own1_last || 'N/A';
            singleParcelRow['Owner 1 First Name'] = parcelDetails.own1_frst || 'N/A';
            singleParcelRow['Owner 2 Last Name'] = parcelDetails.own2_last || 'N/A';
            singleParcelRow['Owner 2 First Name'] = parcelDetails.own2_frst || 'N/A';
            singleParcelRow['Mail Address'] = parcelDetails.mail_str ? 
              `${parcelDetails.mail_nbr || ''} ${parcelDetails.mail_str || ''} ${parcelDetails.mail_unit || ''}`.trim() : 'N/A';
            singleParcelRow['Mail City'] = parcelDetails.mail_city || 'N/A';
            singleParcelRow['Mail State'] = parcelDetails.mail_state || 'N/A';
            singleParcelRow['Mail Zip'] = parcelDetails.mail_zip || 'N/A';
            singleParcelRow['Energy Use'] = parcelDetails.energy_use || 'N/A';
            singleParcelRow['Fuel'] = parcelDetails.fuel || 'N/A';
            singleParcelRow['Sewer'] = parcelDetails.sewer || 'N/A';
            singleParcelRow['Water'] = parcelDetails.water || 'N/A';
          }

          exportData.push(singleParcelRow);
        }
      }

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Favorites');
      
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(data, 'parcel_favorites_detailed.xlsx');
      
      console.log(`Successfully exported ${exportData.length} favorites to Excel`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      // Could add a toast notification here for better user feedback
    } finally {
      setExporting(false);
    }
  };

  const exportToCSV = async () => {
    setExporting(true);
    try {
      const { stateCode, countyName } = getStateAndCounty();
      const csvRows = [];
      
      // Show progress feedback
      console.log(`Starting CSV export of ${favorites.length} favorites...`);

      for (const favorite of favorites) {
        // Normalize parcel IDs from favorite
        let parcelIds = [];
        if (typeof favorite.parcel_ids === 'string') {
          try {
            parcelIds = JSON.parse(favorite.parcel_ids);
            if (!Array.isArray(parcelIds)) {
              parcelIds = [favorite.parcel_ids];
            }
          } catch {
            parcelIds = [favorite.parcel_ids];
          }
        } else if (Array.isArray(favorite.parcel_ids)) {
          parcelIds = favorite.parcel_ids;
        } else if (favorite.parcel_ids) {
          parcelIds = [favorite.parcel_ids];
        }
        
        const isAssemblage = parcelIds.length > 1;

        if (isAssemblage) {
          // For assemblages, create one row with all parcel IDs and FLU info
          const parcelDetails = [];
          for (const parcelId of parcelIds) {
            const details = await fetchParcelDetails(parcelId, stateCode, countyName);
            if (details) {
              parcelDetails.push({
                parcel_id: parcelId,
                land_use_category: details.land_use_category,
                zoning: details.zoning,
                land_acres: details.land_acres,
                assd_imp: details.assd_imp,
                addr: details.addr,
                owner: details.owner
              });
            }
          }

          // Create assemblage row
          const assemblageRow = {
            'Favorite ID': favorite.id,
            'Type': 'Assemblage',
            'Assemblage Tag': `ASM-${favorite.id}`, // Assemblage identifier tag
            'Number of Parcels': parcelIds.length,
            'All Parcel IDs': parcelIds.join(', '),
            'Total Size (Acres)': favorite.size_acres || 'N/A',
            'Future Land Use': favorite.future_land_use || 'N/A',
            'In Place Zoning': favorite.in_place_zoning || 'N/A',
            'Total Improvement Value': favorite.total_improvement_value ? `$${favorite.total_improvement_value.toLocaleString()}` : 'N/A',
            'Center Point': favorite.center_point ? `${favorite.center_point[0]}, ${favorite.center_point[1]}` : 'N/A',
            'Created At': new Date(favorite.created_at).toLocaleDateString(),
            'Updated At': new Date(favorite.updated_at).toLocaleDateString()
          };

          // Add individual parcel details
          parcelDetails.forEach((parcel, index) => {
            assemblageRow[`Parcel ${index + 1} ID`] = parcel.parcel_id;
            assemblageRow[`Parcel ${index + 1} FLU`] = parcel.land_use_category || 'N/A';
            assemblageRow[`Parcel ${index + 1} Zoning`] = parcel.zoning || 'N/A';
            assemblageRow[`Parcel ${index + 1} Acres`] = parcel.land_acres || 'N/A';
            assemblageRow[`Parcel ${index + 1} Address`] = parcel.addr || 'N/A';
            assemblageRow[`Parcel ${index + 1} Owner`] = parcel.owner || 'N/A';
          });

          csvRows.push(assemblageRow);

          // Also add individual rows for each parcel in the assemblage with assemblage tag
          parcelDetails.forEach((parcel, index) => {
            const individualParcelRow = {
              'Favorite ID': favorite.id,
              'Type': 'Individual Parcel (Part of Assemblage)',
              'Assemblage Tag': `ASM-${favorite.id}`, // Same assemblage identifier
              'Parcel ID': parcel.parcel_id,
              'Size (Acres)': parcel.land_acres || 'N/A',
              'Future Land Use': parcel.land_use_category || 'N/A',
              'In Place Zoning': parcel.zoning || 'N/A',
              'Total Improvement Value': parcel.assd_imp ? `$${parcel.assd_imp.toLocaleString()}` : 'N/A',
              'Address': parcel.addr || 'N/A',
              'Owner': parcel.owner || 'N/A',
              'Center Point': favorite.center_point ? `${favorite.center_point[0]}, ${favorite.center_point[1]}` : 'N/A',
              'Created At': new Date(favorite.created_at).toLocaleDateString(),
              'Updated At': new Date(favorite.updated_at).toLocaleDateString(),
              'Parcel Position in Assemblage': `${index + 1} of ${parcelIds.length}`
            };
            csvRows.push(individualParcelRow);
          });
        } else {
          // For single parcels, fetch detailed data
          const parcelId = parcelIds[0];
          const parcelDetails = await fetchParcelDetails(parcelId, stateCode, countyName);

          const singleParcelRow = {
            'Favorite ID': favorite.id,
            'Type': 'Single Parcel',
            'Assemblage Tag': 'N/A', // Not part of an assemblage
            'Parcel ID': parcelId,
            'Size (Acres)': favorite.size_acres || parcelDetails?.land_acres || 'N/A',
            'Future Land Use': favorite.future_land_use || parcelDetails?.land_use_category || 'N/A',
            'In Place Zoning': favorite.in_place_zoning || parcelDetails?.zoning || 'N/A',
            'Total Improvement Value': favorite.total_improvement_value ? `$${favorite.total_improvement_value.toLocaleString()}` : 
                                     (parcelDetails?.assd_imp ? `$${parcelDetails.assd_imp.toLocaleString()}` : 'N/A'),
            'Center Point': favorite.center_point ? `${favorite.center_point[0]}, ${favorite.center_point[1]}` : 'N/A',
            'Created At': new Date(favorite.created_at).toLocaleDateString(),
            'Updated At': new Date(favorite.updated_at).toLocaleDateString()
          };

          // Add full parcel data if available
          if (parcelDetails) {
            singleParcelRow['Address'] = parcelDetails.addr || 'N/A';
            singleParcelRow['Owner'] = parcelDetails.owner || 'N/A';
            singleParcelRow['City'] = parcelDetails.city || 'N/A';
            singleParcelRow['State'] = parcelDetails.state || 'N/A';
            singleParcelRow['Zip Code'] = parcelDetails.zip || 'N/A';
            singleParcelRow['Assessed Land Value'] = parcelDetails.assd_lan ? `$${parcelDetails.assd_lan.toLocaleString()}` : 'N/A';
            singleParcelRow['Market Total Value'] = parcelDetails.mkt_val ? `$${parcelDetails.mkt_val.toLocaleString()}` : 'N/A';
            singleParcelRow['Market Land Value'] = parcelDetails.mkt_lan ? `$${parcelDetails.mkt_lan.toLocaleString()}` : 'N/A';
            singleParcelRow['Market Improvement Value'] = parcelDetails.mkt_imp ? `$${parcelDetails.mkt_imp.toLocaleString()}` : 'N/A';
            singleParcelRow['Tax Amount'] = parcelDetails.tax_amt ? `$${parcelDetails.tax_amt.toLocaleString()}` : 'N/A';
            singleParcelRow['Tax Year'] = parcelDetails.tax_yr || 'N/A';
            singleParcelRow['Assessed Year'] = parcelDetails.assd_yr || 'N/A';
            singleParcelRow['Census Tract'] = parcelDetails.census_tr || 'N/A';
            singleParcelRow['APN'] = parcelDetails.apn || 'N/A';
            singleParcelRow['Formatted APN'] = parcelDetails.frm_apn || 'N/A';
            singleParcelRow['Original APN'] = parcelDetails.orig_apn || 'N/A';
            singleParcelRow['Account Number'] = parcelDetails.acct_no || 'N/A';
            singleParcelRow['Owner 1 Last Name'] = parcelDetails.own1_last || 'N/A';
            singleParcelRow['Owner 1 First Name'] = parcelDetails.own1_frst || 'N/A';
            singleParcelRow['Owner 2 Last Name'] = parcelDetails.own2_last || 'N/A';
            singleParcelRow['Owner 2 First Name'] = parcelDetails.own2_frst || 'N/A';
            singleParcelRow['Mail Address'] = parcelDetails.mail_str ? 
              `${parcelDetails.mail_nbr || ''} ${parcelDetails.mail_str || ''} ${parcelDetails.mail_unit || ''}`.trim() : 'N/A';
            singleParcelRow['Mail City'] = parcelDetails.mail_city || 'N/A';
            singleParcelRow['Mail State'] = parcelDetails.mail_state || 'N/A';
            singleParcelRow['Mail Zip'] = parcelDetails.mail_zip || 'N/A';
            singleParcelRow['Energy Use'] = parcelDetails.energy_use || 'N/A';
            singleParcelRow['Fuel'] = parcelDetails.fuel || 'N/A';
            singleParcelRow['Sewer'] = parcelDetails.sewer || 'N/A';
            singleParcelRow['Water'] = parcelDetails.water || 'N/A';
          }

          csvRows.push(singleParcelRow);
        }
      }

      // Convert to CSV format
      if (csvRows.length > 0) {
        const headers = Object.keys(csvRows[0]);
        const csvContent = [
          headers.join(','),
          ...csvRows.map(row => 
            headers.map(header => {
              const value = row[header] || '';
              // Escape quotes and wrap in quotes if contains comma or quote
              const escapedValue = value.toString().replace(/"/g, '""');
              return `"${escapedValue}"`;
            }).join(',')
          )
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, 'parcel_favorites_detailed.csv');
        
        console.log(`Successfully exported ${csvRows.length} favorites to CSV`);
      }
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      // Could add a toast notification here for better user feedback
    } finally {
      setExporting(false);
    }
  };

  if (loading && favorites.length === 0) {
    return (
      <FavoritesContainer>
        <FavoritesHeader>
          <FavoritesTitle>
            <FaHeart />
            Favorites
          </FavoritesTitle>
        </FavoritesHeader>
        <EmptyState>
          <EmptyIcon>
            <FaHeart />
          </EmptyIcon>
          <p>Loading favorites...</p>
        </EmptyState>
      </FavoritesContainer>
    );
  }

  if (favorites.length === 0) {
    return (
      <FavoritesContainer>
        <FavoritesHeader>
          <FavoritesTitle>
            <FaHeart />
            Favorites
          </FavoritesTitle>
        </FavoritesHeader>
        <EmptyState>
          <EmptyIcon>
            <FaHeart />
          </EmptyIcon>
          <p>No favorites yet</p>
          <p>Click on parcels or assemblages on the map to add them to your favorites</p>
        </EmptyState>
      </FavoritesContainer>
    );
  }

  return (
    <FavoritesContainer>
      <FavoritesHeader>
        <FavoritesTitle>
          <FaHeart />
          Favorites ({totalCount})
        </FavoritesTitle>
        <ExportButtons>
          <ExportButton onClick={exportToExcel} disabled={exporting}>
            {exporting ? (
              <>
                <LoadingSpinner style={{
                  width: '12px',
                  height: '12px',
                  border: '2px solid #f3f3f3',
                  borderTop: '2px solid #667eea',
                  borderRadius: '50%',
                  display: 'inline-block'
                }} />
                Exporting...
              </>
            ) : (
              <>
                <FaFileExcel />
                Excel
              </>
            )}
          </ExportButton>
          <ExportButton onClick={exportToCSV} disabled={exporting}>
            {exporting ? (
              <>
                <LoadingSpinner style={{
                  width: '12px',
                  height: '12px',
                  border: '2px solid #f3f3f3',
                  borderTop: '2px solid #667eea',
                  borderRadius: '50%',
                  display: 'inline-block'
                }} />
                Exporting...
              </>
            ) : (
              <>
                <FaFileCsv />
                CSV
              </>
            )}
          </ExportButton>
        </ExportButtons>
      </FavoritesHeader>

      {error && (
        <div style={{ 
          background: '#f8d7da', 
          color: '#721c24', 
          padding: '0.5rem', 
          borderRadius: '4px', 
          marginBottom: '1rem',
          fontSize: '0.9rem'
        }}>
          {error}
        </div>
      )}

      {favorites.map((item) => (
        <FavoriteItem 
          key={item.id}
          onClick={async () => {
            if (setViewState && item.center_point) {
              try {
                // Move map to center point first
                setViewState({
                  longitude: item.center_point[0],
                  latitude: item.center_point[1],
                  zoom: 16,
                  transitionDuration: 1000 // Smooth transition
                });

                // Wait for map transition to complete before generating GeoJSON
                setTimeout(async () => {
                  try {
                    // Generate and display raster GeoJSON for the favorite's parcels
                    const parcelIds = Array.isArray(item.parcel_ids) ? item.parcel_ids : [item.parcel_ids];
                    const stateCode = filters.state || 'ca';
                    const countyName = filters.county || 'orange';
                    
                    const result = await api.generateParcelBorder(
                      JSON.stringify(parcelIds),
                      stateCode,
                      countyName
                    );

                    if (result.success && result.geojson && setParcelBorder) {
                      // Set the parcel border to display the GeoJSON on the map
                      setParcelBorder(result.geojson);
                    }
                  } catch (error) {
                    console.error('Error generating parcel border for favorite:', error);
                  }
                }, 1200); // Wait 1.2 seconds for map transition to complete
              } catch (error) {
                console.error('Error navigating to favorite location:', error);
              }
            }
          }}
          title="Click to view on map and display parcel boundaries"
        >
          <ItemHeader>
            <ItemTitle>
              Favorite #{item.id}
              <TypeBadge type={Array.isArray(item.parcel_ids) && item.parcel_ids.length > 1 ? 'assemblage' : 'parcel'}>
                {Array.isArray(item.parcel_ids) && item.parcel_ids.length > 1 ? 'Multi' : 'Single'}
              </TypeBadge>
            </ItemTitle>
            <RemoveButton 
              onClick={(e) => {
                e.stopPropagation(); // Prevent triggering the parent click
                deleteFavorite(item.id);
              }}
            >
              <FaTrash />
            </RemoveButton>
          </ItemHeader>
          
          <ItemDetails>
            <DetailField>
              <strong><FaMapMarkerAlt /> Parcel IDs:</strong> 
              {Array.isArray(item.parcel_ids) ? item.parcel_ids.join(', ') : item.parcel_ids}
            </DetailField>
            <DetailField>
              <strong><FaRuler /> Size:</strong> 
              {item.size_acres ? `${item.size_acres} acres` : 'N/A'}
            </DetailField>
            <DetailField>
              <strong>Future Land Use:</strong> 
              {item.future_land_use || 'N/A'}
            </DetailField>
            <DetailField>
              <strong>In Place Zoning:</strong> 
              {item.in_place_zoning || 'N/A'}
            </DetailField>
            <DetailField>
              <strong><FaDollarSign /> Total Improvement Value:</strong> 
              {item.total_improvement_value ? `$${item.total_improvement_value.toLocaleString()}` : 'N/A'}
            </DetailField>
            <DetailField>
              <strong>County ID:</strong> {item.county_id}
            </DetailField>
          </ItemDetails>
        </FavoriteItem>
      ))}

      {hasMore && (
        <div style={{ textAlign: 'center', padding: '1rem' }}>
          <button 
            onClick={loadMoreFavorites}
            disabled={loading}
            style={{
              background: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '0.5rem 1rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
    </FavoritesContainer>
  );
};

export default FavoritesPanel; 